import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Mail,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Paperclip,
  FileText,
  Eye
} from 'lucide-react';
import { workAuthService } from '@/services/workAuthService';
import { format } from 'date-fns';
import type { DocumentResponse } from '@shared/schemas/workAuth';

interface EmailDocument {
  id: string;
  fileName: string;
  filePath?: string;
  fileSize: number;
  documentType: string;
  reviewStatus: string;
  reviewNotes?: string | null;
  submittedBy?: string;
  reviewedBy?: string | null;
  submittedAt: string;
  reviewedAt?: string | null;
  workAuthId?: string;
}

interface IncomingEmail {
  id: string;
  messageId: string;
  subject: string;
  from: string;
  to: string[];
  body: string;
  htmlBody: string | null;
  receivedAt: string;
  processed: boolean;
  processedAt: string | null;
  error: string | null;
  relatedWorkAuthId: string | null;
  createdAt: string;
  documents?: EmailDocument[];
}

interface IncomingEmailsCardProps {
  workAuthId: string;
  onDocumentClick?: (document: DocumentResponse) => void;
  isOpen?: boolean;
  onToggle?: () => void;
}

// Helper to decode HTML entities like &lt; &gt; &amp; etc.
const decodeHtmlEntities = (text: string): string => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

const IncomingEmailsCard: React.FC<IncomingEmailsCardProps> = ({ workAuthId, onDocumentClick, isOpen = true, onToggle }) => {
  const [emails, setEmails] = useState<IncomingEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<IncomingEmail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchEmails = async () => {
    setLoading(true);
    try {
      // Fetch emails related to this work authorization
      const response = await workAuthService.getIncomingEmails(1, 100);
      // Filter to only show emails related to this work auth
      const relatedEmails = response.data.filter(
        (email: IncomingEmail) => email.relatedWorkAuthId === workAuthId
      );
      setEmails(relatedEmails);
    } catch (error) {
      console.error('Failed to load emails:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmails();
  }, [workAuthId]);

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM d, yyyy h:mm a');
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (!text) return '';
    const stripped = text.replace(/<[^>]*>/g, '').trim();
    if (stripped.length <= maxLength) return stripped;
    return stripped.substring(0, maxLength) + '...';
  };

  const openEmailDetail = (email: IncomingEmail) => {
    setSelectedEmail(email);
    setDetailOpen(true);
  };

  const openDocumentPreview = (doc: EmailDocument, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering email detail modal

    if (!onDocumentClick) return;

    // Convert EmailDocument to DocumentResponse for the modal
    const documentResponse: DocumentResponse = {
      id: doc.id,
      workAuthId: doc.workAuthId || workAuthId,
      documentType: doc.documentType as any,
      fileName: doc.fileName,
      filePath: doc.filePath || '',
      fileSize: doc.fileSize,
      mimeType: null,
      reviewStatus: doc.reviewStatus as any,
      reviewNotes: doc.reviewNotes || null,
      submittedBy: doc.submittedBy || 'Email Submission',
      reviewedBy: doc.reviewedBy || null,
      submittedAt: new Date(doc.submittedAt),
      reviewedAt: doc.reviewedAt ? new Date(doc.reviewedAt) : null,
    };

    onDocumentClick(documentResponse);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader className={onToggle ? 'cursor-pointer hover:bg-muted/50 transition-colors' : ''} onClick={onToggle}>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Incoming Emails
            </span>
            {onToggle && <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? '' : '-rotate-90'}`} />}
          </CardTitle>
        </CardHeader>
        {isOpen && (
          <CardContent>
            <div className="flex items-center justify-center py-4">
              <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          </CardContent>
        )}
      </Card>
    );
  }

  if (emails.length === 0) {
    return (
      <Card>
        <CardHeader className={onToggle ? 'cursor-pointer hover:bg-muted/50 transition-colors' : ''} onClick={onToggle}>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Incoming Emails
            </span>
            {onToggle && <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? '' : '-rotate-90'}`} />}
          </CardTitle>
          {isOpen && (
            <CardDescription>
              Emails received related to this work authorization
            </CardDescription>
          )}
        </CardHeader>
        {isOpen && (
          <CardContent>
            <p className="text-sm text-muted-foreground text-center py-4">
              No emails received for this work authorization
            </p>
          </CardContent>
        )}
      </Card>
    );
  }

  const displayedEmails = expanded ? emails : emails.slice(0, 3);

  return (
    <>
      <Card>
        <CardHeader className={onToggle ? 'cursor-pointer hover:bg-muted/50 transition-colors' : ''} onClick={onToggle}>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Incoming Emails
              <Badge variant="secondary" className="ml-2">
                {emails.length}
              </Badge>
            </span>
            {onToggle && <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? '' : '-rotate-90'}`} />}
          </CardTitle>
          {isOpen && (
            <CardDescription>
              Emails received related to this work authorization
            </CardDescription>
          )}
        </CardHeader>
        {isOpen && <CardContent className="space-y-3">
          {displayedEmails.map((email) => (
            <div
              key={email.id}
              className="flex items-start justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
              onClick={() => openEmailDetail(email)}
            >
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="flex-shrink-0 mt-1">
                  {email.error ? (
                    <XCircle className="h-4 w-4 text-red-500" />
                  ) : email.processed ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <Clock className="h-4 w-4 text-yellow-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm truncate">
                      {email.subject || '(No Subject)'}
                    </p>
                    {email.documents && email.documents.length > 0 && (
                      <Badge variant="outline" className="flex-shrink-0 gap-1">
                        <Paperclip className="h-3 w-3" />
                        {email.documents.length}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    From: {email.from}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {truncateText(email.body, 60)}
                  </p>
                </div>
              </div>
              <div className="flex-shrink-0 ml-2">
                <p className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatDate(email.receivedAt)}
                </p>
              </div>
            </div>
          ))}

          {emails.length > 3 && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-4 w-4 mr-2" />
                  Show Less
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 mr-2" />
                  Show {emails.length - 3} More
                </>
              )}
            </Button>
          )}
        </CardContent>}
      </Card>

      {/* Email Detail Modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              {selectedEmail?.subject || '(No Subject)'}
            </DialogTitle>
            <DialogDescription>
              Received {selectedEmail ? formatDate(selectedEmail.receivedAt) : ''}
            </DialogDescription>
          </DialogHeader>

          {selectedEmail && (
            <div className="space-y-4">
              {/* Email metadata */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">From:</span>
                  <p className="font-medium">{selectedEmail.from}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">To:</span>
                  <p className="font-medium">{selectedEmail.to.join(', ')}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Status:</span>
                  <p>
                    {selectedEmail.error ? (
                      <Badge variant="destructive">Error: {selectedEmail.error}</Badge>
                    ) : selectedEmail.processed ? (
                      <Badge variant="default" className="bg-green-600">Processed</Badge>
                    ) : (
                      <Badge variant="secondary">Pending</Badge>
                    )}
                  </p>
                </div>
              </div>

              {/* Attachments */}
              {selectedEmail.documents && selectedEmail.documents.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Paperclip className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">
                      Attachments ({selectedEmail.documents.length})
                    </span>
                  </div>
                  <div className="space-y-2">
                    {selectedEmail.documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-2 bg-muted rounded-md hover:bg-muted/80 cursor-pointer transition-colors"
                        onClick={(e) => openDocumentPreview(doc, e)}
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-blue-500" />
                          <div>
                            <p className="text-sm font-medium">{doc.fileName}</p>
                            <p className="text-xs text-muted-foreground">
                              {(doc.fileSize / 1024).toFixed(1)} KB
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant={doc.reviewStatus === 'APPROVED' ? 'default' : 'secondary'}
                            className={doc.reviewStatus === 'APPROVED' ? 'bg-green-600' : ''}
                          >
                            {doc.reviewStatus}
                          </Badge>
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Email body */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-medium">Message</span>
                </div>
                {selectedEmail.htmlBody ? (
                  <Tabs defaultValue="html">
                    <TabsList>
                      <TabsTrigger value="html">Formatted</TabsTrigger>
                      <TabsTrigger value="text">Plain Text</TabsTrigger>
                    </TabsList>
                    <TabsContent value="html">
                      <ScrollArea className="h-[250px] w-full rounded-md border">
                        <div
                          className="p-4 prose prose-sm max-w-none dark:prose-invert"
                          dangerouslySetInnerHTML={{ __html: selectedEmail.htmlBody }}
                        />
                      </ScrollArea>
                    </TabsContent>
                    <TabsContent value="text">
                      <ScrollArea className="h-[250px] w-full rounded-md border p-4">
                        <pre className="whitespace-pre-wrap text-sm">
                          {decodeHtmlEntities(selectedEmail.body?.replace(/<[^>]*>/g, '') || '(Empty body)')}
                        </pre>
                      </ScrollArea>
                    </TabsContent>
                  </Tabs>
                ) : (
                  <ScrollArea className="h-[250px] w-full rounded-md border p-4">
                    <pre className="whitespace-pre-wrap text-sm">
                      {decodeHtmlEntities(selectedEmail.body?.replace(/<[^>]*>/g, '') || '(Empty body)')}
                    </pre>
                  </ScrollArea>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default IncomingEmailsCard;
